import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/app-layout";
import { LoadMore } from "@/components/load-more";
import { Card, CardBody } from "@/components/ui/card";
import { Alert } from "@/components/ui/alert";
import { AccountSelector } from "@/components/account-selector";
import { StatusBadge } from "@/components/status-badge";
import { useAccounts } from "@/lib/accounts";
import { useHistory } from "@/lib/runs";

function formatDateTime(value: string | null): string {
    return value ? new Date(value).toLocaleString("vi-VN", { dateStyle: "short", timeStyle: "short" }) : "—";
}

export function HistoryPage() {
    const { data: accounts } = useAccounts();
    const [accountId, setAccountId] = useState<string>();

    useEffect(() => {
        const first = accounts?.[0];
        if (!accountId && first) setAccountId(first.id);
    }, [accounts, accountId]);

    const historyQuery = useHistory(accountId);
    const runs = historyQuery.items;
    const isPending = historyQuery.isPending;

    return (
        <>
            <PageHeader title="Lịch sử gửi" description="Mọi lượt gửi và kết quả tới từng nhóm, dùng để đối soát về sau" />

            <div className="space-y-4 p-8">
                {accounts && accounts.length > 1 && (
                    <AccountSelector accounts={accounts} value={accountId} onChange={setAccountId} />
                )}

                {!accounts?.length && <Alert tone="info">Chưa có tài khoản Zalo nào.</Alert>}

                <Card>
                    <CardBody className="space-y-1">
                        {isPending && <p className="py-6 text-center text-sm text-ink-muted">Đang tải…</p>}
                        {runs.length === 0 && (
                            <p className="py-10 text-center text-sm text-ink-muted">Chưa có lượt gửi nào.</p>
                        )}

                        {runs.map((run) => (
                            <Link
                                key={run.id}
                                to={`/runs/${run.id}`}
                                className="flex flex-wrap items-center gap-3 rounded-lg px-2 py-2.5 hover:bg-surface-muted"
                            >
                                <StatusBadge status={run.status} />
                                <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
                                    {run.campaignName}
                                </span>
                                <span className="shrink-0 text-xs text-ink-muted">
                                    {run.sentCount}/{run.totalCount} gửi
                                    {run.failedCount > 0 && (
                                        <span className="text-danger"> · {run.failedCount} lỗi</span>
                                    )}
                                    {run.uncertainCount > 0 && (
                                        <span className="text-warning"> · {run.uncertainCount} chưa rõ</span>
                                    )}
                                </span>
                                <span className="shrink-0 text-xs text-ink-muted">
                                    {formatDateTime(run.finishedAt ?? run.startedAt ?? run.createdAt)}
                                </span>
                            </Link>
                        ))}

                        <LoadMore
                            shown={runs.length}
                            total={historyQuery.total}
                            hasMore={historyQuery.hasMore}
                            loading={historyQuery.isLoadingMore}
                            onLoadMore={historyQuery.loadMore}
                            unit="lượt gửi"
                        />
                    </CardBody>
                </Card>
            </div>
        </>
    );
}
